<template>
  <AppDialog
    class="archive-feedback-dialog"
    position="centered"
    :title="t.dialogTitle"
    show
    @close="closeDialog"
  >
    <div class="title">We value your feedback. Please tell us why you are cancelling.</div>

    <p
      v-if="appSumo"
      class="alert alert-info mb-0 mt-10"
    >
      Since you are on an AppSumo plan this will also archive the organization in Hubstaff Tasks.
    </p>

    <div class="form-group primary-reason-input">
      <label class="control-label">Cancellation reason *</label>
      <AppSelect
        v-model="form.primaryCategory"
        :options="randomizedPrimaryCategories"
        :select2-options="select2PrimaryOptions"
      />
    </div>
    <div
      v-if="secondaryCategories"
      class="form-group secondary-reason-input"
    >
      <label class="control-label">Please specify *</label>
      <AppSelect
        v-model="form.secondaryCategory"
        :options="secondaryCategories"
        :select2-options="select2SecondaryOptions"
      />
    </div>
    <div class="form-group">
      <label class="control-label">What will you be using instead of Hubstaff *</label>
      <AppSelect
        v-model="form.replacement"
        :options="replacementOptions"
        :select2-options="select2ReplacementOptions"
      />
    </div>
    <div class="form-group additional-feedback-input">
      <label class="control-label">Additional feedback *</label>
      <ValidationProvider
        v-slot="{ errors }"
        rules="required|min_feedback:50"
        name="details"
      >
        <div :class="{ 'has-error': errors[0] }">
          <AppTextareaCounter
            v-model="form.details"
            placeholder="Please tell us why you are leaving. We appreciate your feedback."
            :rows="3"
            :error="errors[0]"
          />
        </div>
      </ValidationProvider>
    </div>
    <div class="form-group">
      <label class="control-label">Is there anything Hubstaff could have done to retain you?</label>
      <textarea
        v-model="form.retentionFeedback"
        class="form-control"
      ></textarea>
    </div>
    <div class="modal-footer d-flex justify-content-end pt-30 flex-wrap p-0">
      <a
        class="btn btn-default btn-default-hover-grey"
        :href="changePlanLink"
        :disabled="changePlanDisabled"
        @click.prevent="onChangePlanClick"
      >
        Change plan instead
      </a>
      <a
        class="btn btn-danger"
        :disabled="archiveDisabled"
        @click="submit"
      >
        {{ t.dialogTitle }}
      </a>
    </div>
  </AppDialog>
</template>

<script>
import { AnalyticsResource, OrganizationsResource } from '~/resources';
import { extend } from 'vee-validate';
import { min } from 'vee-validate/dist/rules';
import AppTextareaCounter from '~/components/AppTextareaCounter.vue';

const TEXT = {
  archive: {
    dialogTitle: 'Archive organization',
  },
  cancel: {
    dialogTitle: 'Cancel subscription',
  },
};

extend('min_feedback', {
  ...min,
  message: 'is too short (minimum is 50 characters)',
});

export default {
  name: 'ArchiveFeedbackDialog',
  components: {
    AppTextareaCounter,
  },
  props: {
    appSumo: {
      type: Boolean,
      required: true,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    action: { type: String, default: 'archive' },
    removeCardAfterCancel: { type: Boolean, default: false },
    source: { type: String, default: null },
  },
  data: () => ({
    form: {
      primaryCategory: null,
      secondaryCategory: null,
      details: null,
      replacement: null,
      retentionFeedback: null,
    },
    select2PrimaryOptions: {
      allowClear: true,
      placeholder: 'Select a reason',
      width: '100%',
    },
    select2SecondaryOptions: {
      allowClear: true,
      placeholder: 'Select an option that applies',
      width: '100%',
    },
    select2ReplacementOptions: {
      width: '100%',
    },
    submitting: false,
  }),
  computed: {
    archiveDisabled() {
      return this.submitting || !this.finalCategory || !this.form.replacement || !this.form.details || String(this.form.details).length < 50;
    },
    changePlanDisabled() {
      return this.submitting;
    },
    changePlanLink() {
      return OrganizationsResource.plansLink({ id: this.organizationId });
    },
    finalCategory() {
      return this.secondaryCategories ? this.form.secondaryCategory : this.form.primaryCategory;
    },
    primaryCategories() {
      return window.CANCELLATION_CATEGORIES.primary.filter((value) => value !== 'No reason given');
    },
    replacementOptions() {
      const shuffled = ['Harvest', 'Time Doctor', 'Toggl', 'Apployee', 'Insightful', 'Clockify', 'ActivTrak', 'Desktime'].sort(() => Math.random() - 0.5);

      return ['Nothing', ...shuffled, 'Other'];
    },
    randomizedPrimaryCategories() {
      const otherCategory = this.primaryCategories.find((category) => category === 'Other');
      const otherIndex = this.primaryCategories.indexOf(otherCategory);
      const shuffled = [...this.primaryCategories];

      shuffled.splice(otherIndex, 1);
      shuffled.sort(() => Math.random() - 0.5);

      return [...shuffled, otherCategory];
    },
    secondaryCategories() {
      const primaryCategory = this.form.primaryCategory;

      if (primaryCategory in window.CANCELLATION_CATEGORIES.secondary) {
        const categories = window.CANCELLATION_CATEGORIES.secondary[primaryCategory].map((e) => {
          if (Array.isArray(e)) {
            return { text: e[0], id: e[1] };
          }

          return { text: e, id: e };
        });
        const otherCategory = categories.find(cat => cat.text === 'Other');
        const otherIndex = categories.indexOf(otherCategory);

        if (otherIndex > -1) {
          categories.splice(otherIndex, 1);
          categories.sort(() => Math.random() - 0.5);

          return [...categories, otherCategory];
        }

        return categories.sort(() => Math.random() - 0.5);
      }

      return false;
    },
    t() {
      return TEXT[this.action];
    },
  },
  watch: {
    primaryCategory() {
      this.form.secondaryCategory = null;
    },
  },
  mounted() {
    this.trackEvent('Archive feedback step viewed', {
      action: this.action,
      remove_card_after_cancel: this.removeCardAfterCancel,
    });
  },
  methods: {
    closeDialog(event) {
      this.$emit('closeDialog', event);
    },
    onChangePlanClick() {
      if (this.changePlanDisabled) {
        return;
      }
      this.trackEvent('Change plan instead clicked', {
        action: this.action,
      });
      window.location = this.changePlanLink;
    },
    trackEvent(eventName, extraProperties = {}) {
      return AnalyticsResource.create(null, {
        event_name: eventName,
        event_properties: {
          organization_id: this.organizationId,
          ...(this.source && { source: this.source }),
          ...extraProperties,
        },
      }).catch((error) => console.error('Failed to track event:', error));
    },
    handleAction(data) {
      if (this.action === 'cancel') {
        return OrganizationsResource.cancelSubscription({ id: this.organizationId }, data);
      }

      return OrganizationsResource.archive({ id: this.organizationId }, data);
    },
    submit() {
      if (this.archiveDisabled) {
        return;
      }
      this.submitting = true;

      const data = {
        category: this.finalCategory,
        details: this.form.details,
        retention_feedback: this.form.retentionFeedback,
        replacement: this.form.replacement,
        remove_payment_method: this.removeCardAfterCancel,
      };

      this.handleAction(data)
        .then(async (_response) => {
          const eventName = this.action === 'archive' ? 'Archive organization confirmed' : 'Cancel subscription confirmed';

          await this.trackEvent(eventName, {
            action: this.action,
            remove_card_after_cancel: this.removeCardAfterCancel,
          });
          if (this.action === 'cancel') {
            document.location.reload();
          } else {
            document.location = OrganizationsResource.indexLink();
          }
        })
        .catch((xhr) => {
          const { responseJSON: { error: errorMsg } = {}, statusText } = xhr;

          this.$appMessage(`Error occurred: ${errorMsg || statusText}`);
          this.submitting = false;
        });
    },
  },
};
</script>
